//* Libraries imports
import { motion } from 'framer-motion';

//? Styles imports
import './exam-list-skeleton.scss';

interface ExamListSkeletonProps {
  showTable?: boolean;
}

export function ExamListSkeleton({ showTable = true }: ExamListSkeletonProps) {
  const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  // If showTable is false, show the "in-progress" status skeleton
  if (!showTable) {
    return (
      <motion.div
        className="exam-list-skeleton w-100"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="d-flex justify-content-center align-items-center min-vh-100 w-100">
          <motion.div 
            className="process-status-skeleton"
            variants={itemVariants}
          >
            {/* Icon skeleton */}
            <motion.div
              className="skeleton-status-icon mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Title skeleton */}
            <motion.div
              className="skeleton-status-title mb-3"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Subtitle skeleton */}
            <motion.div
              className="skeleton-status-subtitle mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Message skeleton */}
            <motion.div
              className="skeleton-status-message mb-4"
              variants={skeletonVariants}
              animate="pulse"
            />
            
            {/* Button skeleton */}
            <motion.div
              className="skeleton-status-button"
              variants={skeletonVariants}
              animate="pulse"
            />
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="exam-list-skeleton w-100"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header Section Skeleton */}
      <motion.section 
        className="exam-header-skeleton p-4"
        variants={itemVariants}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            {/* Back Button Skeleton */}
            <motion.div
              className="skeleton-back-button me-3"
              variants={skeletonVariants}
              animate="pulse"
            />
            <div>
              {/* Process Title Skeleton */}
              <motion.div
                className="skeleton-process-title mb-2"
                variants={skeletonVariants}
                animate="pulse"
              />
              {/* Process Info Skeleton */}
              <div className="d-flex gap-3">
                <motion.div
                  className="skeleton-process-info"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-process-info"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-process-info"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>
            </div>
          </div>
          {/* Status Badge Skeleton */}
          <motion.div
            className="skeleton-status-badge"
            variants={skeletonVariants}
            animate="pulse"
          />
        </div>
        
        {/* Process Info Card Skeleton */}
        <motion.div
          className="skeleton-info-card"
          variants={skeletonVariants}
          animate="pulse"
        />
      </motion.section>

      {/* Essay Table Section Skeleton */}
      <motion.section 
        className="exam-table-skeleton p-5"
        variants={itemVariants}
      >        {/* Essay Table Header */}
        <div className="container bg-white p-4 rounded-3 shadow-sm">
          {/* Table Title Skeleton */}
          <motion.div
            className="skeleton-table-title mb-4"
            variants={skeletonVariants}
            animate="pulse"
          />
          
          {/* Search Bar Skeleton */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <motion.div
              className="skeleton-search-input"
              variants={skeletonVariants}
              animate="pulse"
            />
          </div>
          
          {/* Table Content Skeleton */}
          <div className="skeleton-table">
            {/* Table Headers */}
            <div className="skeleton-table-row skeleton-table-head">
              <motion.div
                className="skeleton-table-cell skeleton-table-header-cell"
                variants={skeletonVariants}
                animate="pulse"
              />
              <motion.div
                className="skeleton-table-cell skeleton-table-header-cell"
                variants={skeletonVariants}
                animate="pulse"
              />
              <motion.div
                className="skeleton-table-cell skeleton-table-header-cell skeleton-actions-header"
                variants={skeletonVariants}
                animate="pulse"
              />
            </div>
            
            {/* Table Rows */}
            {[...Array(5)].map((_, rowIndex) => (
              <div key={rowIndex} className="skeleton-table-row">
                <motion.div
                  className="skeleton-table-cell skeleton-table-cell-wide"
                  variants={skeletonVariants}
                  animate="pulse"
                  style={{ animationDelay: `${rowIndex * 0.1}s` }}
                />
                <motion.div
                  className="skeleton-table-cell"
                  variants={skeletonVariants}
                  animate="pulse"
                  style={{ animationDelay: `${rowIndex * 0.1 + 0.05}s` }}
                />
                <motion.div
                  className="skeleton-table-cell skeleton-table-cell-actions"
                  variants={skeletonVariants}
                  animate="pulse"
                  style={{ animationDelay: `${rowIndex * 0.1 + 0.1}s` }}
                />
              </div>
            ))}
          </div>

          {/* Pagination Skeleton */}
          <motion.div
            className="skeleton-pagination-container mt-4"
            variants={itemVariants}
          >
            <div className="d-flex justify-content-between align-items-center">
              {/* Pagination Info Skeleton */}
              <motion.div
                className="skeleton-pagination-info"
                variants={skeletonVariants}
                animate="pulse"
              />
              
              {/* Pagination Controls Skeleton */}
              <div className="d-flex gap-2">
                {[...Array(3)].map((_, index) => (
                  <motion.div
                    key={index}
                    className="skeleton-pagination-button"
                    variants={skeletonVariants}
                    animate="pulse"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </motion.div>
  );
}
